import { ModalHost, ModalClient } from "../libs/modal-host";
import { HtmlLoader } from "../libs/html-loader";
import { RequestHelper } from "../libs/request-helper";
import { Utility } from "../libs/utility";

declare var Hyphenator:any;


export interface ImageGalleryEntry {
	type: string;
	src: string;
}

export class ImageGalleryLoadElement {
	element: HTMLElement;
	caption?: HTMLElement;

	constructor(element: HTMLElement, caption?: HTMLElement) {
		this.element = element;
		this.caption = caption;
	}
}

export class ImageGallery implements ModalClient {
	render(container: HTMLElement): void {
		container.innerHTML = `
<div class='galleryHost'>
	<div class='_spinnerContainer'><div class='spinner'></div></div>
	<div class='_close'><span class='icon-close'></span>Schließen</div>
	<div class='_nav _prev icon-arrow-1-left'></div>
	<div class='_content'></div>
	<div class='_nav _next icon-arrow-1-right'></div>
	<div class='_caption'></div>
	<div class='_counter calendarMini'></div>
</div>`;

		this.container = container;

		const galleryHost = this.container.querySelector(".galleryHost");

		if(galleryHost == null) {
			throw "Could not find generated host";
		}

		galleryHost.addEventListener("click", (e: Event) => this.onModalClick(e as MouseEvent));

		galleryHost.addEventListener("touchstart", (e: TouchEvent) => this.onModalTouchStart(e));
		galleryHost.addEventListener("touchmove", (e: TouchEvent) => this.onModalTouchMove(e));
		galleryHost.addEventListener("touchend", (e: TouchEvent) => this.onModalTouchEnd(e));

		this.galleryHost = galleryHost as HTMLDivElement;

		if(this.elements.length < 2) {
			const navElements = galleryHost.getElementsByClassName("_nav");

			for(let i = 0; i < navElements.length; ++i) {
				const navElement = navElements[i] as HTMLElement;
				navElement.style.display = "none";
			}
		}

		this.load();
	}

	canClose(): boolean {
		return true;
	}

	close(): void {
		this.container = undefined;
		this.galleryHost = undefined;
	}

	private elements: ImageGalleryEntry[] = [];
	private currentIndex = -1;
	private container: HTMLElement | undefined;
	protected galleryHost: HTMLDivElement | undefined;
	private currentTouchStartX: number | undefined;
	private currentTouchX: number | undefined;

	constructor(private target: HTMLElement) {

		this.target.addEventListener("click", () => this.onClick());

		const idsRaw = this.target.getAttribute("data-gallery-ids");

		if(idsRaw == null) {
			return;
		}

		const ids = idsRaw.split(",");

		for(const id of ids) {
			const partPos = id.indexOf(":");

			if(partPos < 1 || partPos == (id.length - 1)) {
				console.log(`Invalid gallery id: ${id}`);
				continue;
			}

			const typePart = id.substr(0, partPos);
			const srcPart = id.substr(partPos + 1);

			this.elements.push({
				type: typePart,
				src: srcPart
			})
		}

		if(this.elements.length > 0) {
			this.currentIndex = 0;
		}
	}

	protected async loadElement(element: ImageGalleryEntry): Promise<ImageGalleryLoadElement> {
		if(element.type == "img") {
			return new ImageGalleryLoadElement(await this.loadImage(element));
		}
		else if(element.type == "youtube") {
			return new ImageGalleryLoadElement(await this.loadYoutube(element));
		}
		else if(element.type == "video") {
			return new ImageGalleryLoadElement(await this.loadVideo(element));
		}
		else {
			return new ImageGalleryLoadElement(this.createErrorNode("_internalLoadError"));
		}
	}

	private async load() {
		const element = this.elements[this.currentIndex];

		if(this.container == null) {
			return;
		}

		const spinner = this.container.querySelector("._spinnerContainer");
		const content = this.container.querySelector("._content");
		const counter = this.container.querySelector("._counter");
		const caption = this.container.querySelector("._caption");

		if(spinner == null || content == null || counter == null || caption == null) {
			return;
		}

		//TODO: move to deactivate?
		while(content.firstChild) {
			content.removeChild(content.firstChild);
		}

		while(caption.firstChild) {
			caption.removeChild(caption.firstChild);
		}

		Utility.toggleClass(spinner, "_spinning", true);
		Utility.toggleClass(this.galleryHost!, "_spinning", true);

		//await Utility.delay(10000);

		const newContent = await this.loadElement(element);

		newContent.element.classList.add("_contentInner");

		content.appendChild(newContent.element);

		if(newContent.caption) {
			caption.appendChild(newContent.caption);
		}

		counter.innerHTML = `${this.currentIndex + 1} von ${this.elements.length}`;

		Utility.toggleClass(spinner, "_spinning", false);
		Utility.toggleClass(this.galleryHost!, "_spinning", false);

		this.position();
		Hyphenator.run();

	}

	protected position() {

	}

	private async loadImage(element: ImageGalleryEntry): Promise<HTMLElement> {
		try {
			const image = await HtmlLoader.loadImage(element.src);
			image.className = "_imageEntry";
			return image;
		}
		catch(e) {
			return this.createErrorNode("_imageLoadError");
		}
	}

	private async loadYoutube(element: ImageGalleryEntry): Promise<HTMLElement> {
		try {
			const iframe = await HtmlLoader.loadIFrame(`https://www.youtube.com/embed/${element.src}?autoplay=0`);
			iframe.className = "_youtubeEntry";
			iframe.setAttribute("allow", "fullscreen");
			iframe.setAttribute("mozallowfullscreen", "mozallowfullscreen");
			iframe.setAttribute("msallowfullscreen", "msallowfullscreen");
			iframe.setAttribute("webkitallowfullscreen", "webkitallowfullscreen");
			iframe.setAttribute("oallowfullscreen", "oallowfullscreen");
			iframe.setAttribute("allowfullscreen", "1");
			return iframe;
		}
		catch(e) {
			console.log(e);
			return this.createErrorNode("_videoLoadError");
		}
	}

	private async loadVideo(element: ImageGalleryEntry): Promise<HTMLElement>{
		try{
			const video = await HtmlLoader.loadIFrame(element.src)
			return video
		}
		catch(e){
			return this.createErrorNode("_videoLoadError");
		}
	}

	protected createErrorNode(errorName: string): HTMLElement {
		const errorNode = document.createElement("div");
		errorNode.classList.add("_errorMessage");
		errorNode.classList.add(errorName);
		return errorNode;
	}

	private onClick() {

		if(this.currentIndex < 0) {
			return;
		}

		ModalHost.displayModal(this);

		const ids = this.target.getAttribute("data-gallery-ids");
	}

	private onModalTouchStart(event: TouchEvent) {

		this.currentTouchX = undefined;

		if(event.touches.length != 1) {
			this.currentTouchStartX = undefined;
		}
		else {
			this.currentTouchStartX = event.touches[0].screenX;
		}
	}

	private onModalTouchMove(event: TouchEvent) {

		if(event.touches.length != 1) {
			return;
		}

		this.currentTouchX = event.touches[0].screenX;

		event.preventDefault();
	}

	private onModalTouchEnd(event: TouchEvent) {
		if(this.currentTouchStartX === undefined || this.currentTouchX === undefined || this.currentTouchStartX == this.currentTouchX) {
			this.currentTouchStartX = undefined;
			this.currentTouchX = undefined;
			return;
		}

		if((this.currentTouchX - this.currentTouchStartX) < 0) {
			this.onNextClick();
		}
		else {
			this.onPrevClick();
		}

		event.preventDefault();
	}

	private onModalClick(event: MouseEvent) {
		let current : Element | null = event.target as Element;

		let handled = false;

		while(current) {

			if(current === this.container) {
				break;
			}

			if(current.tagName == "A") {
				event.stopImmediatePropagation();
				return;
			}

			for(const cssClass of current.classList) {
				switch(cssClass) {
					case "_next":
						this.onNextClick();
						handled = true;
						break;

					case "_prev":
						this.onPrevClick();
						handled = true;
						break;

					case "_contentInner":
						handled = true;
						var contentInner = this.container!.querySelector("._contentInner");



						if(contentInner) {
							const isRight = event.offsetX > (contentInner.clientWidth / 2);

							if(isRight) {
								this.onNextClick();
							}
							else {
								this.onPrevClick();
							}
						}

						break;

					case "_close":
						ModalHost.tryClose();
						handled = true;
						break;

					case "_caption":
					case "_counter":
						handled = true;
						break;
				}
			}

			if(handled) {
				break;
			}

			current = current.parentElement;
		}

		if(!handled) {
			ModalHost.tryClose();
		}

		event.preventDefault();
		event.stopImmediatePropagation();
	}

	private onNextClick() {
		if(++this.currentIndex >= this.elements.length) {
			this.currentIndex = 0;
		}

		this.load();
	}

	private onPrevClick() {
		if(--this.currentIndex < 0) {
			this.currentIndex = this.elements.length - 1;
		}

		this.load();
	}
}
